import React, { useCallback, useEffect, useState } from 'react'

import { Theaters } from '@mui/icons-material'
import { CircularProgress, Grid, LinearProgress } from '@mui/material'
import { Button, Title, useNotify, useRefresh, useTranslate } from 'react-admin'

import { API } from '../../helpers/Api.helper'

export const ImportsAmendArtwork = ({
  importId,
  importTitle,
  onChange,
}: ImportsAmendArtworkProps): JSX.Element => {
  const notify = useNotify()
  const refresh = useRefresh()
  const translate = useTranslate()

  const [isLoading, setIsLoading] = useState(false)
  const [artworkCreationJobId, setArtworkCreationJobId] = useState<string>()
  const [progress, setProgress] = useState(0)

  const onAllArtworksCreate = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await API.post(`/api/youtubeRawImports/${importId}/artworksCreationJob`, {})

      setArtworkCreationJobId(response.data.job.id)
      setProgress(response.data.job.progress)
    } catch (e: any) {
      setIsLoading(false)
      notify(
        `${translate('pages.importsAmendment.allArtworksCreationError')} - ${
          e.response.data?.message || e.response.data
        }`,
        { type: 'error' },
      )
    }
  }, [importId, notify, translate])

  const refreshArtworkCreationJobStatus = useCallback(async () => {
    if (artworkCreationJobId && isLoading) {
      try {
        const response = await API.get(
          `/api/youtubeRawImports/${importId}/artworksCreationJob/${artworkCreationJobId}`,
          {},
        )

        const p = (response.data as any)?.job.progress

        setProgress(p)
        if (p >= 100) {
          onChange()
          setIsLoading(false)
          notify('pages.importsAmendment.allArtworksCreationSuccess', {
            type: 'success',
          })

          refresh()
        }
      } catch (e: any) {
        setIsLoading(false)
        setArtworkCreationJobId(undefined)
        setProgress(0)

        notify(
          `${translate('pages.importsAmendment.allArtworksCreationError')} - ${
            e.response.data?.message || e.response.data
          }`,
          { type: 'error' },
        )
      }
    }
  }, [artworkCreationJobId, isLoading, importId, onChange, notify, refresh, translate])

  useEffect(() => {
    const interval = setInterval(() => {
      refreshArtworkCreationJobStatus()
    }, 200)

    return () => clearInterval(interval)
  }, [refreshArtworkCreationJobStatus])

  return (
    <>
      <Title title={`${translate('pages.importsAmendment._title')} / ${importTitle}`} />
      <Grid
        container
        direction="column"
        spacing={4}
        justifyContent="center"
        alignItems="center"
        style={{ marginTop: '1rem' }}
      >
        <Grid item>
          <Button variant="contained" onClick={onAllArtworksCreate} disabled={isLoading}>
            <>
              {isLoading ? (
                <CircularProgress size="1rem" color="inherit" style={{ marginRight: '1rem' }} />
              ) : (
                <Theaters style={{ marginRight: '1rem' }} />
              )}
              {isLoading
                ? translate('pages.importsAmendment.creationInProgress')
                : translate('pages.importsAmendment.createAllArtworks')}
            </>
          </Button>
        </Grid>
        <Grid item width="50%">
          <LinearProgress
            variant="determinate"
            value={progress}
            color={progress >= 100 ? 'success' : 'primary'}
            style={{
              height: '0.5rem',
              opacity: progress === 0 ? 0 : 1,
            }}
          />
        </Grid>
      </Grid>
    </>
  )
}

type ImportsAmendArtworkProps = {
  importId: string
  importTitle: string
  onChange: () => void
}
